import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getRechargePackagesAdmin, updateRechargePackagesAdmin } from '../../api';
import { PageHeader, TableSkeleton, EmptyState } from '../../components/ui';
import { Package, Plus, RefreshCw, Save, Trash2, GripVertical, IndianRupee } from 'lucide-react';

type PackRow = {
  id: string;
  label: string;
  amountRupees: number;
  bonusRupees: number;
  isPopular: boolean;
  isActive: boolean;
  sortOrder: number;
};

function normalizePack(p: any, i: number): PackRow {
  return {
    id: String(p.id || `pack_${i}`),
    label: String(p.label || ''),
    amountRupees: Number(p.amountRupees ?? p.amountDisplay ?? p.amount ?? 0),
    bonusRupees: Number(p.bonusRupees ?? p.bonusDisplay ?? p.bonus ?? 0),
    isPopular: !!p.isPopular,
    isActive: p.isActive !== false,
    sortOrder: Number(p.sortOrder ?? (i + 1) * 10),
  };
}

function newPack(index: number): PackRow {
  return {
    id: `pack_${Date.now()}_${index}`,
    label: 'New pack',
    amountRupees: 100,
    bonusRupees: 0,
    isPopular: false,
    isActive: true,
    sortOrder: (index + 1) * 10,
  };
}

export default function RechargePackagesPage() {
  const qc = useQueryClient();
  const [packs, setPacks] = useState<PackRow[]>([]);
  const [allowCustomAmount, setAllowCustomAmount] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-recharge-packages'],
    queryFn: () => getRechargePackagesAdmin().then((r) => r.data.data),
  });

  useEffect(() => {
    if (!data) return;
    setAllowCustomAmount(!!data.allowCustomAmount);
    setPacks((data.packs || []).map(normalizePack));
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      updateRechargePackagesAdmin({
        allowCustomAmount,
        packs: packs.map((p, i) => ({
          id: p.id,
          label: p.label.trim() || `Pack ₹${p.amountRupees}`,
          sortOrder: p.sortOrder || (i + 1) * 10,
          amountRupees: Math.max(1, Math.round(Number(p.amountRupees) || 0)),
          bonusRupees: Math.max(0, Math.round(Number(p.bonusRupees) || 0)),
          isPopular: !!p.isPopular,
          isActive: p.isActive !== false,
        })),
      }),
    onSuccess: (res) => {
      toast.success('Recharge pack costing saved');
      const d = res.data.data;
      setAllowCustomAmount(!!d.allowCustomAmount);
      setPacks((d.packs || []).map(normalizePack));
      qc.invalidateQueries({ queryKey: ['admin-recharge-packages'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const patch = (id: string, next: Partial<PackRow>) => {
    setPacks((prev) => prev.map((p) => (p.id === id ? { ...p, ...next } : p)));
  };

  const remove = (id: string) => {
    setPacks((prev) => prev.filter((p) => p.id !== id));
  };

  const move = (id: string, dir: -1 | 1) => {
    setPacks((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx < 0) return prev;
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const copy = [...prev];
      const tmp = copy[idx];
      copy[idx] = copy[j];
      copy[j] = tmp;
      return copy.map((p, i) => ({ ...p, sortOrder: (i + 1) * 10 }));
    });
  };

  const activePacks = packs.filter((p) => p.isActive);

  return (
    <div className="p-6">
      <PageHeader
        title="Wallet recharge packs"
        subtitle="Set what users pay and the bonus credited to their wallet"
      >
        <button type="button" className="btn-secondary btn-sm" onClick={() => refetch()}>
          <RefreshCw size={14} />
        </button>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => setPacks((prev) => [...prev, newPack(prev.length)])}
        >
          <Plus size={14} />
          Add pack
        </button>
        <button
          type="button"
          className="btn-primary btn-sm"
          disabled={save.isPending || packs.length === 0}
          onClick={() => save.mutate()}
        >
          <Save size={14} />
          {save.isPending ? 'Saving…' : 'Save costing'}
        </button>
      </PageHeader>

      {isError ? (
        <div className="card p-5 mb-5 text-center">
          <p className="text-sm text-red-600 font-medium">Could not load recharge packs</p>
          <p className="text-xs text-gray-500 mt-1">{(error as any)?.response?.data?.message || (error as Error)?.message}</p>
          <button type="button" className="btn-primary btn-sm mt-3" onClick={() => refetch()}>Retry</button>
        </div>
      ) : null}

      {/* Costing summary */}
      <div className="card overflow-hidden mb-5">
        <div className="px-5 py-3 border-b border-cream-300 bg-cream-100 flex items-center gap-2">
          <IndianRupee size={16} className="text-primary-600" />
          <h3 className="text-sm font-bold text-ink">Pack costing (active)</h3>
        </div>
        {isLoading && packs.length === 0 ? (
          <div className="p-4"><TableSkeleton rows={4} cols={4} /></div>
        ) : activePacks.length === 0 ? (
          <EmptyState message="No active packs — add or enable a pack below" icon={<Package size={28} />} />
        ) : (
          <table className="w-full">
            <thead className="bg-cream-50 border-b border-cream-200">
              <tr>
                <th className="table-th">Pack</th>
                <th className="table-th">User pays</th>
                <th className="table-th">Bonus</th>
                <th className="table-th">Wallet credit</th>
              </tr>
            </thead>
            <tbody>
              {activePacks.map((p) => (
                <tr key={p.id} className="table-row">
                  <td className="table-td">
                    <span className="font-semibold text-ink">{p.label}</span>
                    {p.isPopular ? <span className="ml-2 badge-orange">Popular</span> : null}
                  </td>
                  <td className="table-td font-semibold">₹{Number(p.amountRupees).toLocaleString('en-IN')}</td>
                  <td className="table-td text-green-700">
                    {p.bonusRupees > 0 ? `+₹${Number(p.bonusRupees).toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td className="table-td font-bold text-primary-700">
                    ₹{(Number(p.amountRupees) + Number(p.bonusRupees)).toLocaleString('en-IN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card p-4 mb-5 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-ink/80">
          <input
            type="checkbox"
            checked={allowCustomAmount}
            onChange={(e) => setAllowCustomAmount(e.target.checked)}
          />
          Allow custom amounts (no pack match → ₹0 bonus)
        </label>
        <p className="text-xs text-ink/40">
          Inactive packs are hidden in the app. Duplicate amounts are dropped on save.
        </p>
      </div>

      {isLoading && packs.length === 0 ? (
        <TableSkeleton rows={6} cols={5} />
      ) : (
        <div className="space-y-3">
          {packs.map((pack, index) => (
            <div key={pack.id} className={`card p-4 ${pack.isActive ? '' : 'opacity-60'}`}>
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex flex-col gap-1 pt-1">
                  <button type="button" className="text-ink/40 hover:text-ink" onClick={() => move(pack.id, -1)} title="Move up">
                    <GripVertical size={14} />
                  </button>
                  <span className="text-[10px] text-ink/40 text-center">{index + 1}</span>
                  <button type="button" className="text-ink/40 hover:text-ink" onClick={() => move(pack.id, 1)} title="Move down">
                    <GripVertical size={14} className="rotate-180" />
                  </button>
                </div>

                <div className="flex items-center gap-2 min-w-[140px]">
                  <Package size={16} className="text-primary-600" />
                  <div>
                    <label className="block text-[10px] text-ink/50 mb-0.5">Label</label>
                    <input
                      className="input py-1.5 text-sm min-w-[140px]"
                      value={pack.label}
                      onChange={(e) => patch(pack.id, { label: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] text-ink/50 mb-0.5">User pays (₹)</label>
                  <input
                    type="number"
                    min={1}
                    className="input py-1.5 text-sm w-28"
                    value={pack.amountRupees}
                    onChange={(e) => patch(pack.id, { amountRupees: Number(e.target.value) })}
                  />
                </div>

                <div>
                  <label className="block text-[10px] text-ink/50 mb-0.5">Bonus (₹)</label>
                  <input
                    type="number"
                    min={0}
                    className="input py-1.5 text-sm w-28"
                    value={pack.bonusRupees}
                    onChange={(e) => patch(pack.id, { bonusRupees: Number(e.target.value) })}
                  />
                </div>

                <div className="pt-5 text-sm font-semibold text-primary-700 min-w-[120px]">
                  Credit ₹{(Number(pack.amountRupees) + Number(pack.bonusRupees)).toLocaleString('en-IN')}
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-5">
                  <label className="flex items-center gap-2 text-sm text-ink/80">
                    <input
                      type="checkbox"
                      checked={pack.isPopular}
                      onChange={(e) => patch(pack.id, { isPopular: e.target.checked })}
                    />
                    Popular
                  </label>
                  <label className="flex items-center gap-2 text-sm text-ink/80">
                    <input
                      type="checkbox"
                      checked={pack.isActive}
                      onChange={(e) => patch(pack.id, { isActive: e.target.checked })}
                    />
                    Active
                  </label>
                  <button
                    type="button"
                    className="text-primary-700 hover:text-primary-800 text-xs flex items-center gap-1"
                    onClick={() => remove(pack.id)}
                    disabled={packs.length <= 1}
                  >
                    <Trash2 size={13} />
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
